import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Link2, X } from 'lucide-react';

interface VanityUrlDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  uploadId: string;
  currentVanityUrl: string | null;
  shortCode: string;
  onSuccess: () => void;
}

export function VanityUrlDialog({
  open,
  onOpenChange,
  uploadId,
  currentVanityUrl,
  shortCode,
  onSuccess,
}: VanityUrlDialogProps) {
  const [vanityUrl, setVanityUrl] = useState(currentVanityUrl || '');
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/uploads/${uploadId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({ vanityUrl: vanityUrl || null }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update vanity URL');
      }

      toast.success(vanityUrl ? 'Vanity URL set' : 'Vanity URL removed');
      onSuccess();
      onOpenChange(false);
    } catch (error: unknown) {
      const err = error as { message?: string };
      toast.error(err.message || 'Failed to update vanity URL');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setVanityUrl('');
  };

  const baseUrl = window.location.origin;
  const previewUrl = vanityUrl || shortCode;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Custom Short URL</DialogTitle>
          <DialogDescription>
            Create a memorable custom URL for your upload. Leave blank to use the
            default short code.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="vanity-url">Custom URL</Label>
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-2 rounded-md border bg-muted px-3 py-2 text-sm">
                <span className="text-muted-foreground">{baseUrl}/</span>
                <Input
                  id="vanity-url"
                  value={vanityUrl}
                  onChange={(e) => setVanityUrl(e.target.value)}
                  placeholder={shortCode}
                  className="border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  maxLength={50}
                />
              </div>
              {vanityUrl && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClear}
                  title="Clear"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              3-50 characters: letters, numbers, hyphens, and underscores only
            </p>
          </div>

          <div className="space-y-2">
            <Label>Preview</Label>
            <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm break-all">
              {baseUrl}/{previewUrl}
            </div>
          </div>

          {currentVanityUrl && (
            <div className="rounded-md bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 px-3 py-2 text-sm">
              <div className="flex items-start gap-2">
                <Link2 className="h-4 w-4 mt-0.5 text-blue-600 dark:text-blue-400" />
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-100">
                    Current custom URL
                  </p>
                  <p className="text-blue-700 dark:text-blue-300">
                    {baseUrl}/{currentVanityUrl}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
